import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils';

import { zodCompanySettingsSchema } from '../useMutationUpdateCompanySettings/validations';

import { CompanyAiSettings, UpdateCompanyAiSettingsResponse } from './types';
import {
  zodUpdateCompanyAiSettingsEndpointSchema,
  zodUpdateCompanyAiSettingsSchema,
} from './validations';

/**
 * Updates a company's AI model settings.
 *
 * @param companyId - The ID of the company to update.
 * @param aiModelSettings - An object containing the AI model settings to be updated.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/set_ai_model_settings_vadmin_company__company_id__default_ai_model_settings_put
 */
export const updateCompanyAiSettings = async (
  companyId: UUID,
  aiModelSettings: CompanyAiSettings,
) => {
  try {
    const response = await vestigasApi
      .put(ENDPOINT.COMPANY.AI_SETTINGS.UPDATE(companyId), {
        json: aiModelSettings,
        validate: {
          body: zodUpdateCompanyAiSettingsSchema
            .transform((data) => {
              if (
                data.customSettings &&
                data.customSettings.modelChoice !== 'di_plus_ai_foundry'
              ) {
                // Remove diPlusAiFoundryFieldsToReplace unless modelChoice is 'di_plus_ai_foundry'
                const { diPlusAiFoundryFieldsToReplace: _, ...rest } =
                  data.customSettings;

                return {
                  ...data,
                  customSettings: rest,
                };
              }

              return data;
            })
            .transform((data) =>
              withoutObjectKeysWhereValueIs(data, [null, undefined, '']),
            ),
          pathSegments: zodUpdateCompanyAiSettingsEndpointSchema,
          response: zodCompanySettingsSchema,
        },
      })
      .json<UpdateCompanyAiSettingsResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error updating company',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
